import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaw,
  faCat,
  faDog,
  faHeart,
  faStar,
  faWeight,
  faRuler,
  faCalendarAlt,
  faBrain,
  faChild,
  faCut,
  faUsers,
  faBolt,
  faSpinner,
  faExclamationTriangle,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import { useBreedMatches } from '../hooks/useBreedMatches';
import { clearQuizData } from '../utils/quizStorage';

/**
 * Component to display breed recommendations based on quiz responses
 */
function BreedRecommendations({ userPreferences, limit = 5 }) {
  const { matches, loading, error, refetch } = useBreedMatches(userPreferences, limit);
  const navigate = useNavigate();

  const handleRetakeQuiz = () => {
    // Clear existing quiz data
    clearQuizData();
    // Navigate to quiz page
    navigate('/quiz');
  };

  const handleFindPets = (breed) => {
    // Navigate to search page with breed preference
    navigate('/search', { 
      state: { 
        breedPreference: breed.name,
        animalType: breed.type === 'cat' ? 'cat' : 'dog'
      } 
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-500';
    if (rating >= 3) return 'text-amber-500';
    return 'text-neutral-400';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (score >= 60) return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
    return 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200';
  };

  const renderRatingStars = (rating) => {
    if (!rating) return <span className="text-neutral-400">N/A</span>;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FontAwesomeIcon
            key={star}
            icon={faStar}
            className={`h-3 w-3 ${star <= rating ? getRatingColor(rating) : 'text-neutral-300 dark:text-neutral-600'}`}
          />
        ))}
        <span className="text-xs text-neutral-500 ml-1">{rating}/5</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
        <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-primary-500 animate-spin mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          Finding Your Perfect Matches
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300">
          We&apos;re analyzing thousands of breeds to find the best matches for your lifestyle...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
        <FontAwesomeIcon icon={faExclamationTriangle} className="h-8 w-8 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          Unable to Load Recommendations
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300 mb-4">
          {error}
        </p>
        <Button variant="primary" onClick={refetch}>
          Try Again
        </Button>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-8 text-center">
        <FontAwesomeIcon icon={faPaw} className="h-8 w-8 text-neutral-400 mb-4" />
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
          No Matches Found
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300">
          We couldn&apos;t find any breeds that match your preferences. Try retaking the quiz with different answers.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-2">
          Your Top {matches.length} Breed Recommendations
        </h2>
        <p className="text-neutral-600 dark:text-neutral-300">
          Based on your quiz responses, here are the breeds that best match your lifestyle and preferences.
        </p>
      </div>

      <div className="space-y-6">
        {matches.map((breed, index) => (
          <div
            key={breed.id}
            className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Image and Basic Info */}
              <div className="lg:w-1/3">
                <div className="relative">
                  <img
                    src={breed.image}
                    alt={breed.name}
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = `https://via.placeholder.com/300x200/e5e7eb/6b7280?text=${encodeURIComponent(breed.name)}`;
                    }}
                  />
                  <div className="absolute top-2 left-2 flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(breed.score)}`}>
                      {breed.score}% Match
                    </span>
                    <div className="bg-white dark:bg-neutral-800 rounded-full p-1">
                      <FontAwesomeIcon
                        icon={breed.type === 'dog' ? faDog : faCat}
                        className={`h-4 w-4 ${breed.type === 'dog' ? 'text-amber-600' : 'text-primary-600'}`}
                      />
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 bg-white dark:bg-neutral-800 rounded-full px-2 py-1">
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      #{index + 1}
                    </span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="lg:w-2/3">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                    {breed.name}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-300 text-sm mb-3">
                    {breed.description}
                  </p>
                  
                  {/* Basic Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {breed.lifeSpan && (
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faCalendarAlt} className="h-4 w-4 text-neutral-400" />
                        <span className="text-sm text-neutral-600 dark:text-neutral-300">
                          {breed.lifeSpan} years
                        </span>
                      </div>
                    )}
                    {breed.weight && (
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faWeight} className="h-4 w-4 text-neutral-400" />
                        <span className="text-sm text-neutral-600 dark:text-neutral-300">
                          {breed.weight} kg
                        </span>
                      </div>
                    )}
                    {breed.height && (
                      <div className="flex items-center space-x-2">
                        <FontAwesomeIcon icon={faRuler} className="h-4 w-4 text-neutral-400" />
                        <span className="text-sm text-neutral-600 dark:text-neutral-300">
                          {breed.height} cm
                        </span>
                      </div>
                    )}
                    {breed.origin && (
                      <div className="text-sm text-neutral-600 dark:text-neutral-300">
                        Origin: {breed.origin}
                      </div>
                    )}
                  </div>
                </div>

                {/* Personality Traits */}
                <div className="mb-4">
                  <h4 className="font-semibold text-neutral-900 dark:text-white mb-2">
                    Temperament
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {breed.temperament?.split(',').slice(0, 6).map((trait, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-xs"
                      >
                        {trait.trim()}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Ratings Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {breed.energyLevel && (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <FontAwesomeIcon icon={faBolt} className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">Energy</span>
                      </div>
                      {renderRatingStars(breed.energyLevel)}
                    </div>
                  )}
                  {breed.affectionLevel && (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <FontAwesomeIcon icon={faHeart} className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">Affection</span>
                      </div>
                      {renderRatingStars(breed.affectionLevel)}
                    </div>
                  )}
                  {breed.childFriendly && (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <FontAwesomeIcon icon={faChild} className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">Kid Friendly</span>
                      </div>
                      {renderRatingStars(breed.childFriendly)}
                    </div>
                  )}
                  {breed.grooming && (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <FontAwesomeIcon icon={faCut} className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">Grooming</span>
                      </div>
                      {renderRatingStars(breed.grooming)}
                    </div>
                  )}
                  {breed.intelligence && (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <FontAwesomeIcon icon={faBrain} className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">Intelligence</span>
                      </div>
                      {renderRatingStars(breed.intelligence)}
                    </div>
                  )}
                  {breed.socialNeeds && (
                    <div>
                      <div className="flex items-center space-x-1 mb-1">
                        <FontAwesomeIcon icon={faUsers} className="h-3 w-3 text-neutral-400" />
                        <span className="text-xs text-neutral-600 dark:text-neutral-400">Social Needs</span>
                      </div>
                      {renderRatingStars(breed.socialNeeds)}
                    </div>
                  )}
                </div>

                {/* Special Traits */}
                {(breed.hypoallergenic || breed.hairless || breed.rare) && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {breed.hypoallergenic && (
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs">
                        Hypoallergenic
                      </span>
                    )}
                    {breed.hairless && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                        Hairless
                      </span>
                    )}
                    {breed.rare && (
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs">
                        Rare Breed
                      </span>
                    )}
                  </div>
                )}

                {/* Find Adoptable Pets Button */}
                <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <Button 
                    variant="primary" 
                    size="small"
                    onClick={() => handleFindPets(breed)}
                    className="w-full"
                  >
                    <FontAwesomeIcon icon={faSearch} className="mr-2" />
                    Find Adoptable {breed.name}s
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
          These recommendations are based on your quiz responses and breed characteristics from The Dog API and The Cat API.
        </p>
        <Button variant="outline" size="small" onClick={handleRetakeQuiz}>
          Retake Quiz for New Matches
        </Button>
      </div>
    </div>
  );
}

BreedRecommendations.propTypes = {
  userPreferences: PropTypes.object.isRequired,
  limit: PropTypes.number,
};

export default BreedRecommendations;